// Single source of truth for the localized, SEO-friendly content URLs — the
// rules pages and, further down, the landing / FAQ / legal pages.
//
// Pure TypeScript — NO React, NO `window`, NO i18n singleton — so it can be
// imported both by the app (Vite) and by the Node prerender/sitemap scripts
// (tsx) without side effects.
//
// URL shape: /{lang}/{segment}/{slug}   e.g. /it/regole/dama-turca
//            /{lang}/{segment}          e.g. /en/rules        (index page)
//            /{lang}/                   e.g. /it/            (landing)
//            /                                               (landing, english)
//
// Slugs are deliberately HAND-AUTHORED ASCII kebab-case keywords (not derived
// from translations at runtime): they are load-bearing SEO terms and stable
// URLs. Changing a translation must never silently change a published URL.

import { GameVariant, ACTIVE_VARIANTS } from '@draughts/shared';

export const RULES_LANGS = ['en', 'it', 'nl', 'de', 'fr', 'es', 'pl', 'pt-BR'] as const;
export type RulesLang = (typeof RULES_LANGS)[number];

export const BASE_URL = 'https://draughtsgame.com';

/** x-default hreflang target language. */
export const DEFAULT_LANG: RulesLang = 'en';

/**
 * Primo segmento dell'URL per ogni lingua. Coincide col codice tranne dove il
 * codice porta una regione: i path sono case-sensitive, quindi `/pt-BR/` sarebbe
 * una trappola per chi digita l'indirizzo. L'hreflang e `<html lang>` continuano
 * a usare il codice, che è la forma BCP-47 corretta.
 */
export const LANG_SEGMENT: Record<RulesLang, string> = {
  en: 'en',
  it: 'it',
  nl: 'nl',
  de: 'de',
  fr: 'fr',
  es: 'es',
  pl: 'pl',
  'pt-BR': 'pt-br',
};

/** Localized path segment that means "rules" for each language. */
export const RULES_SEGMENT: Record<RulesLang, string> = {
  en: 'rules',
  it: 'regole',
  nl: 'regels',
  de: 'regeln',
  fr: 'regles',
  es: 'reglas',
  pl: 'zasady',
  'pt-BR': 'regras',
};

/**
 * Localized last segment of the opening-tables sub-page, e.g.
 * /it/regole/dama-italiana/tabelle-di-apertura. Only the two variants with an
 * official F.I.D. ballot have one — see `VARIANTS_WITH_OPENINGS`.
 */
export const OPENINGS_SEGMENT: Record<RulesLang, string> = {
  en: 'opening-tables',
  it: 'tabelle-di-apertura',
  nl: 'openingstabellen',
  de: 'eroeffnungstabellen',
  fr: 'tables-d-ouverture',
  es: 'tablas-de-apertura',
  pl: 'tabele-otwarc',
  'pt-BR': 'tabelas-de-abertura',
};

/** Variants whose rules page has an opening-tables sub-page. */
export const VARIANTS_WITH_OPENINGS: readonly GameVariant[] = [GameVariant.ITALIAN, GameVariant.ENGLISH];

export function hasOpeningsPage(variant: GameVariant): boolean {
  return VARIANTS_WITH_OPENINGS.includes(variant);
}

/**
 * Localized last segment of the detailed capture-rules sub-page, e.g.
 * /it/regole/dama-italiana/regole-di-presa. Only the variants whose capture
 * precedence is a rule set in its own right have one — see
 * `VARIANTS_WITH_CAPTURE_RULES`.
 */
export const CAPTURE_SEGMENT: Record<RulesLang, string> = {
  en: 'capture-rules',
  it: 'regole-di-presa',
  nl: 'slagregels',
  de: 'schlagregeln',
  fr: 'regles-de-prise',
  es: 'reglas-de-captura',
  pl: 'zasady-bicia',
  'pt-BR': 'regras-de-captura',
};

/**
 * Variants whose rules page has a detailed capture-rules sub-page. The three
 * that need one: italian and triestine carry the four-level F.I.D. precedence
 * ladder, english is the counter-example where capture is mandatory but the
 * choice is free.
 */
export const VARIANTS_WITH_CAPTURE_RULES: readonly GameVariant[] = [
  GameVariant.ITALIAN,
  GameVariant.TRIESTINE,
  GameVariant.ENGLISH,
];

export function hasCaptureRulesPage(variant: GameVariant): boolean {
  return VARIANTS_WITH_CAPTURE_RULES.includes(variant);
}

/**
 * Ultimo segmento della pagina delle REGOLE DI GIOCO, es. /it/regole/come-si-gioca:
 * l'elenco delle varianti, che fino a ieri era l'indice della sezione.
 *
 * Come le regole dei tornei, questa pagina NON appartiene a una variante: sta al
 * secondo posto, la stessa casella che per tutte le altre e' lo slug della
 * variante. Percio' il parser la guarda PRIMA dello slug — vedi
 * `parseRulesPath` — e `routes.test.ts` verifica che nessuna lingua abbia uno
 * slug variante uguale a questo segmento, ne' lo stesso segmento dei tornei.
 *
 * Le otto parole non sono scelte a caso: sono la coda che ogni lingua ha GIA'
 * nel proprio `rules.seo.indexTitle` («— come si gioca», «— Spielanleitung»,
 * «— jak grac»), cioe' la forma in cui quella lingua pone la domanda. Un
 * `gioco`/`game` sarebbe stato simmetrico a `tornei` ma muto: «how to play X»
 * e' una ricerca vera, «game» e' un sostantivo.
 */
export const GAME_RULES_SEGMENT: Record<RulesLang, string> = {
  en: 'how-to-play',
  it: 'come-si-gioca',
  nl: 'hoe-te-spelen',
  de: 'spielanleitung',
  fr: 'comment-jouer',
  es: 'como-jugar',
  pl: 'jak-grac',
  'pt-BR': 'como-se-joga',
};

/**
 * Ultimo segmento della sotto-pagina delle regole dei tornei, es.
 * /it/regole/tornei.
 *
 * A differenza di aperture e prese, questa pagina NON appartiene a una variante:
 * sta al secondo posto, la stessa casella che per tutte le altre e' lo slug
 * della variante. Percio' il parser la guarda PRIMA dello slug — vedi
 * `parseRulesPath` — e `routes.test.ts` verifica che nessuna lingua abbia uno
 * slug variante uguale a questo segmento.
 */
export const TOURNAMENT_RULES_SEGMENT: Record<RulesLang, string> = {
  en: 'tournaments',
  it: 'tornei',
  nl: 'toernooien',
  de: 'turniere',
  fr: 'tournois',
  es: 'torneos',
  pl: 'turnieje',
  'pt-BR': 'torneios',
};

/**
 * Localized URL slug per variant per language. ASCII only (no diacritics in
 * URLs). The word for "draughts" is woven in per language for keyword strength
 * (dama / draughts / dammen / dame / dames / damas / warcaby).
 */
export const VARIANT_SLUGS: Record<RulesLang, Record<GameVariant, string>> = {
  en: {
    [GameVariant.INTERNATIONAL]: 'international-draughts',
    [GameVariant.ITALIAN]: 'italian-draughts',
    [GameVariant.ENGLISH]: 'english-draughts',
    [GameVariant.BRAZILIAN]: 'brazilian-draughts',
    [GameVariant.FRISIAN]: 'frisian-draughts',
    [GameVariant.FRYSK]: 'frysk-draughts',
    [GameVariant.TURKISH]: 'turkish-draughts',
    [GameVariant.ARMENIAN]: 'armenian-draughts',
    [GameVariant.RUSSIAN]: 'russian-draughts',
    [GameVariant.SPANISH]: 'spanish-draughts',
    [GameVariant.TRIESTINE]: 'triestine-draughts',
    [GameVariant.CANADIAN]: 'canadian-draughts',
    [GameVariant.NIGERIAN]: 'nigerian-draughts',
    [GameVariant.GHANAIAN]: 'ghanaian-draughts',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'czech-draughts',
    [GameVariant.THAI]: 'thai-draughts',
    [GameVariant.PORTUGUESE]: 'portuguese-draughts',
    [GameVariant.FILIPINO]: 'filipino-draughts',
    [GameVariant.MALAYSIAN]: 'malaysian-draughts',
    [GameVariant.GERMAN]: 'german-draughts',
    [GameVariant.JAMAICAN]: 'jamaican-draughts',
    [GameVariant.SINHALESE]: 'sinhalese-draughts',
    [GameVariant.SOUTH_AFRICAN]: 'south-african-draughts',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'hexdame',
  },
  it: {
    [GameVariant.INTERNATIONAL]: 'dama-internazionale',
    [GameVariant.ITALIAN]: 'dama-italiana',
    [GameVariant.ENGLISH]: 'dama-inglese',
    [GameVariant.BRAZILIAN]: 'dama-brasiliana',
    [GameVariant.FRISIAN]: 'dama-frisone',
    [GameVariant.FRYSK]: 'dama-frysk',
    [GameVariant.TURKISH]: 'dama-turca',
    [GameVariant.ARMENIAN]: 'dama-armena',
    [GameVariant.RUSSIAN]: 'dama-russa',
    [GameVariant.SPANISH]: 'dama-spagnola',
    [GameVariant.TRIESTINE]: 'dama-triestina',
    [GameVariant.CANADIAN]: 'dama-canadese',
    [GameVariant.NIGERIAN]: 'dama-nigeriana',
    [GameVariant.GHANAIAN]: 'dama-ghanese',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'dama-ceca',
    [GameVariant.THAI]: 'dama-thailandese',
    [GameVariant.PORTUGUESE]: 'dama-portoghese',
    [GameVariant.FILIPINO]: 'dama-filippina',
    [GameVariant.MALAYSIAN]: 'dama-malese',
    [GameVariant.GERMAN]: 'dama-tedesca',
    [GameVariant.JAMAICAN]: 'dama-giamaicana',
    [GameVariant.SINHALESE]: 'dama-singalese',
    [GameVariant.SOUTH_AFRICAN]: 'dama-sudafricana',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'dama-esagonale',
  },
  nl: {
    [GameVariant.INTERNATIONAL]: 'internationaal-dammen',
    [GameVariant.ITALIAN]: 'italiaans-dammen',
    [GameVariant.ENGLISH]: 'engels-dammen',
    [GameVariant.BRAZILIAN]: 'braziliaans-dammen',
    [GameVariant.FRISIAN]: 'fries-dammen',
    [GameVariant.FRYSK]: 'frysk-dammen',
    [GameVariant.TURKISH]: 'turks-dammen',
    [GameVariant.ARMENIAN]: 'armeens-dammen',
    [GameVariant.RUSSIAN]: 'russisch-dammen',
    [GameVariant.SPANISH]: 'spaans-dammen',
    [GameVariant.TRIESTINE]: 'triestijns-dammen',
    [GameVariant.CANADIAN]: 'canadees-dammen',
    [GameVariant.NIGERIAN]: 'nigeriaans-dammen',
    [GameVariant.GHANAIAN]: 'ghanees-dammen',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'tsjechisch-dammen',
    [GameVariant.THAI]: 'thais-dammen',
    [GameVariant.PORTUGUESE]: 'portugees-dammen',
    [GameVariant.FILIPINO]: 'filipijns-dammen',
    [GameVariant.MALAYSIAN]: 'maleisisch-dammen',
    [GameVariant.GERMAN]: 'duits-dammen',
    [GameVariant.JAMAICAN]: 'jamaicaans-dammen',
    [GameVariant.SINHALESE]: 'singalees-dammen',
    [GameVariant.SOUTH_AFRICAN]: 'zuid-afrikaans-dammen',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'hexdammen',
  },
  de: {
    [GameVariant.INTERNATIONAL]: 'internationales-dame',
    [GameVariant.ITALIAN]: 'italienisches-dame',
    [GameVariant.ENGLISH]: 'englisches-dame',
    [GameVariant.BRAZILIAN]: 'brasilianisches-dame',
    [GameVariant.FRISIAN]: 'friesisches-dame',
    [GameVariant.FRYSK]: 'frysk-dame',
    [GameVariant.TURKISH]: 'tuerkisches-dame',
    [GameVariant.ARMENIAN]: 'armenisches-dame',
    [GameVariant.RUSSIAN]: 'russisches-dame',
    [GameVariant.SPANISH]: 'spanisches-dame',
    [GameVariant.TRIESTINE]: 'triestiner-dame',
    [GameVariant.CANADIAN]: 'kanadisches-dame',
    [GameVariant.NIGERIAN]: 'nigerianisches-dame',
    [GameVariant.GHANAIAN]: 'ghanaisches-dame',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'tschechisches-dame',
    [GameVariant.THAI]: 'thailaendisches-dame',
    [GameVariant.PORTUGUESE]: 'portugiesisches-dame',
    [GameVariant.FILIPINO]: 'philippinisches-dame',
    [GameVariant.MALAYSIAN]: 'malaysisches-dame',
    [GameVariant.GERMAN]: 'deutsches-dame',
    [GameVariant.JAMAICAN]: 'jamaikanisches-dame',
    [GameVariant.SINHALESE]: 'singhalesisches-dame',
    [GameVariant.SOUTH_AFRICAN]: 'suedafrikanisches-dame',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'hexdame',
  },
  fr: {
    [GameVariant.INTERNATIONAL]: 'dames-internationales',
    [GameVariant.ITALIAN]: 'dames-italiennes',
    [GameVariant.ENGLISH]: 'dames-anglaises',
    [GameVariant.BRAZILIAN]: 'dames-bresiliennes',
    [GameVariant.FRISIAN]: 'dames-frisonnes',
    [GameVariant.FRYSK]: 'dames-frysk',
    [GameVariant.TURKISH]: 'dames-turques',
    [GameVariant.ARMENIAN]: 'dames-armeniennes',
    [GameVariant.RUSSIAN]: 'dames-russes',
    [GameVariant.SPANISH]: 'dames-espagnoles',
    [GameVariant.TRIESTINE]: 'dames-triestines',
    [GameVariant.CANADIAN]: 'dames-canadiennes',
    [GameVariant.NIGERIAN]: 'dames-nigerianes',
    [GameVariant.GHANAIAN]: 'dames-ghaneennes',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'dames-tcheques',
    [GameVariant.THAI]: 'dames-thailandaises',
    [GameVariant.PORTUGUESE]: 'dames-portugaises',
    [GameVariant.FILIPINO]: 'dames-philippines',
    [GameVariant.MALAYSIAN]: 'dames-malaisiennes',
    [GameVariant.GERMAN]: 'dames-allemandes',
    [GameVariant.JAMAICAN]: 'dames-jamaicaines',
    [GameVariant.SINHALESE]: 'dames-cingalaises',
    [GameVariant.SOUTH_AFRICAN]: 'dames-sud-africaines',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'dames-hexagonales',
  },
  es: {
    [GameVariant.INTERNATIONAL]: 'damas-internacionales',
    [GameVariant.ITALIAN]: 'damas-italianas',
    [GameVariant.ENGLISH]: 'damas-inglesas',
    [GameVariant.BRAZILIAN]: 'damas-brasilenas',
    [GameVariant.FRISIAN]: 'damas-frisonas',
    [GameVariant.FRYSK]: 'damas-frysk',
    [GameVariant.TURKISH]: 'damas-turcas',
    [GameVariant.ARMENIAN]: 'damas-armenias',
    [GameVariant.RUSSIAN]: 'damas-rusas',
    [GameVariant.SPANISH]: 'damas-espanolas',
    [GameVariant.TRIESTINE]: 'damas-triestinas',
    [GameVariant.CANADIAN]: 'damas-canadienses',
    [GameVariant.NIGERIAN]: 'damas-nigerianas',
    [GameVariant.GHANAIAN]: 'damas-ghanesas',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'damas-checas',
    [GameVariant.THAI]: 'damas-tailandesas',
    [GameVariant.PORTUGUESE]: 'damas-portuguesas',
    [GameVariant.FILIPINO]: 'damas-filipinas',
    [GameVariant.MALAYSIAN]: 'damas-malasias',
    [GameVariant.GERMAN]: 'damas-alemanas',
    [GameVariant.JAMAICAN]: 'damas-jamaicanas',
    [GameVariant.SINHALESE]: 'damas-cingalesas',
    [GameVariant.SOUTH_AFRICAN]: 'damas-sudafricanas',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'damas-hexagonales',
  },
  pl: {
    [GameVariant.INTERNATIONAL]: 'warcaby-miedzynarodowe',
    [GameVariant.ITALIAN]: 'warcaby-wloskie',
    [GameVariant.ENGLISH]: 'warcaby-angielskie',
    [GameVariant.BRAZILIAN]: 'warcaby-brazylijskie',
    [GameVariant.FRISIAN]: 'warcaby-fryzyjskie',
    [GameVariant.FRYSK]: 'warcaby-frysk',
    [GameVariant.TURKISH]: 'warcaby-tureckie',
    [GameVariant.ARMENIAN]: 'warcaby-ormianskie',
    [GameVariant.RUSSIAN]: 'warcaby-rosyjskie',
    [GameVariant.SPANISH]: 'warcaby-hiszpanskie',
    [GameVariant.TRIESTINE]: 'warcaby-triesteenskie',
    [GameVariant.CANADIAN]: 'warcaby-kanadyjskie',
    [GameVariant.NIGERIAN]: 'warcaby-nigeryjskie',
    [GameVariant.GHANAIAN]: 'warcaby-ghanskie',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'warcaby-czeskie',
    [GameVariant.THAI]: 'warcaby-tajskie',
    [GameVariant.PORTUGUESE]: 'warcaby-portugalskie',
    [GameVariant.FILIPINO]: 'warcaby-filipinskie',
    [GameVariant.MALAYSIAN]: 'warcaby-malezyjskie',
    [GameVariant.GERMAN]: 'warcaby-niemieckie',
    [GameVariant.JAMAICAN]: 'warcaby-jamajskie',
    [GameVariant.SINHALESE]: 'warcaby-syngaleskie',
    [GameVariant.SOUTH_AFRICAN]: 'warcaby-poludniowoafrykanskie',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'warcaby-szesciokatne',
  },
  'pt-BR': {
    [GameVariant.INTERNATIONAL]: 'damas-internacionais',
    [GameVariant.ITALIAN]: 'damas-italianas',
    [GameVariant.ENGLISH]: 'damas-inglesas',
    [GameVariant.BRAZILIAN]: 'damas-brasileiras',
    [GameVariant.FRISIAN]: 'damas-frisias',
    [GameVariant.FRYSK]: 'damas-frysk',
    [GameVariant.TURKISH]: 'damas-turcas',
    [GameVariant.ARMENIAN]: 'damas-armenias',
    [GameVariant.RUSSIAN]: 'damas-russas',
    [GameVariant.SPANISH]: 'damas-espanholas',
    [GameVariant.TRIESTINE]: 'damas-triestinas',
    [GameVariant.CANADIAN]: 'damas-canadenses',
    [GameVariant.NIGERIAN]: 'damas-nigerianas',
    [GameVariant.GHANAIAN]: 'damas-ganesas',
    [GameVariant.POOL]: 'pool-checkers',
    [GameVariant.CZECH]: 'damas-tchecas',
    [GameVariant.THAI]: 'damas-tailandesas',
    [GameVariant.PORTUGUESE]: 'damas-portuguesas',
    [GameVariant.FILIPINO]: 'damas-filipinas',
    [GameVariant.MALAYSIAN]: 'damas-malaias',
    [GameVariant.GERMAN]: 'damas-alemas',
    [GameVariant.JAMAICAN]: 'damas-jamaicanas',
    [GameVariant.SINHALESE]: 'damas-cingalesas',
    [GameVariant.SOUTH_AFRICAN]: 'damas-sul-africanas',
    [GameVariant.DAMEO]: 'dameo',
    [GameVariant.CRODA]: 'croda',
    [GameVariant.HEX]: 'damas-hexagonais',
  },
};

// --- Reverse lookup tables (built once at module load) ----------------------

const segmentToLang: Record<string, RulesLang> = {};
for (const lang of RULES_LANGS) segmentToLang[RULES_SEGMENT[lang]] = lang;

const langSegmentToLang: Record<string, RulesLang> = {};
for (const lang of RULES_LANGS) langSegmentToLang[LANG_SEGMENT[lang]] = lang;

const slugToVariant: Record<RulesLang, Record<string, GameVariant>> = {
  en: {}, it: {}, nl: {}, de: {}, fr: {}, es: {}, pl: {}, 'pt-BR': {},
};
for (const lang of RULES_LANGS) {
  for (const variant of ACTIVE_VARIANTS) {
    slugToVariant[lang][VARIANT_SLUGS[lang][variant]] = variant;
  }
}

// --- Builders ---------------------------------------------------------------

export function isRulesLang(value: string): value is RulesLang {
  return (RULES_LANGS as readonly string[]).includes(value);
}

/**
 * Un tag qualsiasi ricondotto a una lingua che ha davvero delle pagine regole.
 * Combacia prima esatto, poi sul tag base ('pt' → 'pt-BR', 'it-IT' → 'it'), e
 * solo in ultimo ripiega sul default: costruire un URL morto è peggio.
 */
function toRulesLang(lang: string): RulesLang {
  if (isRulesLang(lang)) return lang;
  const base = lang.split('-')[0]?.toLowerCase();
  return RULES_LANGS.find((code) => code.split('-')[0] === base) ?? DEFAULT_LANG;
}

export function buildRulesIndexPath(lang: RulesLang): string {
  return `/${LANG_SEGMENT[lang]}/${RULES_SEGMENT[lang]}`;
}

/**
 * Le regole di GIOCO: l'elenco delle varianti. Firma STRETTA come
 * `buildTournamentRulesPath`, e per la stessa ragione — i chiamanti sono le
 * pagine regole e App, che la lingua l'hanno gia' normalizzata.
 */
export function buildGameRulesPath(lang: RulesLang): string {
  return `${buildRulesIndexPath(lang)}/${GAME_RULES_SEGMENT[lang]}`;
}

export function buildRulesPath(lang: RulesLang, variant: GameVariant): string {
  return `/${LANG_SEGMENT[lang]}/${RULES_SEGMENT[lang]}/${VARIANT_SLUGS[lang][variant]}`;
}

/**
 * Opening-tables page for a variant. Takes a loose language string (callers
 * outside the rules screens hold `i18n.language`) and falls back to the default.
 */
export function buildOpeningsPath(lang: string, variant: GameVariant | string): string {
  const l = toRulesLang(lang);
  const v = (typeof variant === 'string' ? variant.toUpperCase() : variant) as GameVariant;
  return `${buildRulesPath(l, v)}/${OPENINGS_SEGMENT[l]}`;
}

/**
 * Detailed capture-rules page for a variant. Same loose-language contract as
 * `buildOpeningsPath`.
 */
export function buildCapturePath(lang: string, variant: GameVariant | string): string {
  const l = toRulesLang(lang);
  const v = (typeof variant === 'string' ? variant.toUpperCase() : variant) as GameVariant;
  return `${buildRulesPath(l, v)}/${CAPTURE_SEGMENT[l]}`;
}

/**
 * Regole dei tornei. Firma STRETTA, non lasca come le due qui sopra: quelle la
 * prendono lasca perche' le chiamano schermate di gioco che hanno in mano
 * `i18n.language` grezzo, mentre qui i chiamanti sono le pagine regole e App,
 * che la lingua l'hanno gia' normalizzata con `isRulesLang`.
 */
export function buildTournamentRulesPath(lang: RulesLang): string {
  return `${buildRulesIndexPath(lang)}/${TOURNAMENT_RULES_SEGMENT[lang]}`;
}

// --- Parser -----------------------------------------------------------------

export type ParsedRulesPath =
  | { lang: RulesLang; index: true }
  | { lang: RulesLang; gameRules: true }
  | { lang: RulesLang; tournaments: true }
  | { lang: RulesLang; variant: GameVariant; openings?: true; capture?: true };

/**
 * Parse a pathname into a rules location, or null for any non-rules path
 * (so all other app routes are left untouched).
 */
export function parseRulesPath(pathname: string): ParsedRulesPath | null {
  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);
  // [lang, segment], [lang, segment, slug] or [lang, segment, slug, openings]
  if (parts.length < 2 || parts.length > 4) return null;
  const langSegment = parts[0];
  const segmentPart = parts[1];
  if (langSegment === undefined || segmentPart === undefined) return null;
  const langPart = langSegmentToLang[langSegment];
  if (langPart === undefined) return null;
  if (segmentToLang[segmentPart] !== langPart) return null;
  if (parts.length === 2) return { lang: langPart, index: true };
  const slugPart = parts[2];
  if (slugPart === undefined) return null;
  /*
   * Le regole dei tornei occupano la stessa casella di uno slug variante, ed e'
   * per questo che si guardano PRIMA: uno slug che un giorno valesse `tornei`
   * farebbe sparire una pagina gia' pubblicata e indicizzata, invece di dare un
   * conflitto. Con il controllo qui il peggio che capita e' che quella variante
   * diventa irraggiungibile, e c'e' un test che lo pesca.
   */
  if (slugPart === TOURNAMENT_RULES_SEGMENT[langPart]) {
    return parts.length === 3 ? { lang: langPart, tournaments: true } : null;
  }
  /*
   * Stessa casella, stessa ragione. L'ordine FRA questi due non e' osservabile:
   * i due segmenti non possono coincidere, e c'e' un test che lo pianta. Quello
   * che conta e' che vengano entrambi prima di `slugToVariant`.
   */
  if (slugPart === GAME_RULES_SEGMENT[langPart]) {
    return parts.length === 3 ? { lang: langPart, gameRules: true } : null;
  }
  const variant = slugToVariant[langPart][slugPart];
  if (!variant) return null;
  if (parts.length === 3) return { lang: langPart, variant };
  // Only the variants that own a sub-page have one; any other 4th segment is a
  // 404, not a rules route, so it must keep falling through to the SPA.
  if (parts[3] === OPENINGS_SEGMENT[langPart] && hasOpeningsPage(variant)) {
    return { lang: langPart, variant, openings: true };
  }
  if (parts[3] === CAPTURE_SEGMENT[langPart] && hasCaptureRulesPage(variant)) {
    return { lang: langPart, variant, capture: true };
  }
  return null;
}

// --- Pagine di contenuto non-regole -----------------------------------------
//
// Stessa convenzione degli slug delle varianti: ASCII kebab-case scritti a mano,
// perche' sono URL pubblicati e non devono cambiare quando cambia una traduzione.
//
// La landing e' l'eccezione alla forma /{lang}/{segmento}: l'inglese sta sulla
// radice `/` (e' il dominio stesso, e togliergliela per darla a /en/ vorrebbe
// dire o un duplicato o un redirect, e nginx non e' versionato), le altre sette
// lingue stanno su /{lang}/. `/en/` NON esiste: chi lo digita cade nel fallback
// di nginx e riceve la radice, che e' gia' la pagina giusta.

/** Pagine di contenuto che non sono le regole. */
export type ContentPage = 'landing' | 'faq' | 'terms' | 'privacy';

/**
 * L'elenco, per chi deve percorrerle tutte: lo script di prerender e il
 * generatore del sitemap. Vive accanto al tipo cosi' aggiungerne una rompe
 * entrambi in fase di build invece di dimenticarne una in silenzio.
 */
export const CONTENT_PAGES: readonly ContentPage[] = ['landing', 'faq', 'terms', 'privacy'];

export const FAQ_SEGMENT: Record<RulesLang, string> = {
  en: 'faq',
  it: 'domande-frequenti',
  nl: 'veelgestelde-vragen',
  de: 'haeufige-fragen',
  fr: 'questions-frequentes',
  es: 'preguntas-frecuentes',
  pl: 'czesto-zadawane-pytania',
  'pt-BR': 'perguntas-frequentes',
};

export const TERMS_SEGMENT: Record<RulesLang, string> = {
  en: 'terms',
  it: 'termini-di-servizio',
  nl: 'gebruiksvoorwaarden',
  de: 'nutzungsbedingungen',
  fr: 'conditions-utilisation',
  es: 'terminos-de-servicio',
  pl: 'regulamin',
  'pt-BR': 'termos-de-servico',
};

export const PRIVACY_SEGMENT: Record<RulesLang, string> = {
  en: 'privacy',
  it: 'informativa-privacy',
  nl: 'privacybeleid',
  de: 'datenschutz',
  fr: 'confidentialite',
  es: 'privacidad',
  pl: 'prywatnosc',
  'pt-BR': 'privacidade',
};

const PAGE_SEGMENTS: Record<Exclude<ContentPage, 'landing'>, Record<RulesLang, string>> = {
  faq: FAQ_SEGMENT,
  terms: TERMS_SEGMENT,
  privacy: PRIVACY_SEGMENT,
};

/** Landing: `/` per l'inglese, `/{lang}/` per le altre sette. */
export function buildLandingPath(lang: RulesLang): string {
  return lang === DEFAULT_LANG ? '/' : `/${LANG_SEGMENT[lang]}/`;
}

export function buildFaqPath(lang: RulesLang): string {
  return `/${LANG_SEGMENT[lang]}/${FAQ_SEGMENT[lang]}`;
}

export function buildTermsPath(lang: RulesLang): string {
  return `/${LANG_SEGMENT[lang]}/${TERMS_SEGMENT[lang]}`;
}

export function buildPrivacyPath(lang: RulesLang): string {
  return `/${LANG_SEGMENT[lang]}/${PRIVACY_SEGMENT[lang]}`;
}

/** Il percorso di una qualsiasi pagina di contenuto, nella lingua data. */
export function buildContentPath(lang: RulesLang, page: ContentPage): string {
  switch (page) {
    case 'landing':
      return buildLandingPath(lang);
    case 'faq':
      return buildFaqPath(lang);
    case 'terms':
      return buildTermsPath(lang);
    case 'privacy':
      return buildPrivacyPath(lang);
  }
}

export type ParsedContentPath =
  | { kind: 'rules'; rules: ParsedRulesPath }
  | {
      kind: 'page';
      lang: RulesLang;
      page: ContentPage;
      /**
       * Se l'URL dichiarava davvero una lingua. La radice `/` no: e' la landing
       * inglese per un crawler, ma per chi arriva col browser e' solo «il sito»,
       * e la lingua scelta in precedenza deve continuare a vincere. Senza questa
       * distinzione, ogni ritorno sulla home rimetterebbe tutto in inglese.
       */
      explicitLang: boolean;
    };

/**
 * Il parser completo degli URL di contenuto: regole, landing, FAQ e legali.
 * Restituisce null per tutto il resto, cosi' le altre rotte dell'app (i token di
 * verifica, i link profondi in query string) restano intatte.
 */
export function parseContentPath(pathname: string): ParsedContentPath | null {
  const rules = parseRulesPath(pathname);
  if (rules) return { kind: 'rules', rules };

  const parts = pathname.replace(/\/+$/, '').split('/').filter(Boolean);

  // Radice: la landing inglese. `/en/` di proposito non e' qui — non esiste.
  if (parts.length === 0) {
    return { kind: 'page', lang: DEFAULT_LANG, page: 'landing', explicitLang: false };
  }

  const langSegment = parts[0];
  if (langSegment === undefined) return null;
  const lang = langSegmentToLang[langSegment];
  if (lang === undefined) return null;

  // `/it/`, `/nl/`, … — la landing localizzata. L'inglese vive sulla radice, e
  // `/en/` deve restare non riconosciuto perche' non viene mai pubblicato.
  if (parts.length === 1) {
    return lang === DEFAULT_LANG ? null : { kind: 'page', lang, page: 'landing', explicitLang: true };
  }

  if (parts.length !== 2) return null;
  const segment = parts[1];
  if (segment === undefined) return null;
  for (const page of ['faq', 'terms', 'privacy'] as const) {
    if (PAGE_SEGMENTS[page][lang] === segment) {
      return { kind: 'page', lang, page, explicitLang: true };
    }
  }
  return null;
}
